import { getCountryCallingCode } from "libphonenumber-js"

import { PHONE_AUTH_COUNTRIES, type CountryCode } from "@/lib/phone-utils"
import IconChevronDown from "@/components/icons/chevron-down"
import { InputGroupButton } from "@/components/ui/input-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select"

const countryNames = new Intl.DisplayNames(["en"], { type: "region" })
const phoneCountries = PHONE_AUTH_COUNTRIES.map((code) => ({
  code,
  name: countryNames.of(code) ?? code,
  dialCode: `+${getCountryCallingCode(code)}`,
}))

/** The calling-code button at the start of a phone input group. Lists the countries phone sign-in supports. */
export function PhoneCountryPicker({
  value,
  onValueChange,
  disabled,
}: {
  value: CountryCode
  onValueChange: (value: CountryCode) => void
  disabled?: boolean
}) {
  const selected = phoneCountries.find((country) => country.code === value)

  return (
    <Select
      value={value}
      onValueChange={(next) => {
        const country = phoneCountries.find(({ code }) => code === next)
        if (country) onValueChange(country.code)
      }}
      disabled={disabled}
    >
      <SelectTrigger
        render={
          <InputGroupButton
            variant="subtle"
            disabled={disabled}
            aria-label={`Country or region: ${selected?.name ?? value} (${selected?.dialCode ?? ""})`}
            className="gap-1 px-[.75em]"
          >
            <span>{selected?.dialCode}</span>
            <IconChevronDown className="size-3 stroke-[2.5]" />
          </InputGroupButton>
        }
      />
      <SelectContent align="start" alignItemWithTrigger={false}>
        {phoneCountries.map((country) => (
          <SelectItem key={country.code} value={country.code}>
            <span className="inline-block w-9 font-semibold tabular-nums">
              {country.dialCode}
            </span>
            {country.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
